import isEmpty from "lodash/isEmpty";
import type { SettingsStore } from "../../stores/settings";
import { SURVEY_TYPES, surveyStore, updateSurvey, type SurveyStore } from "../../stores/survey";
import { get } from "svelte/store";
import { navigateTo } from "../../routing";
import { NavigationType } from "../../stores/session";
import { saveUserSettings } from "../../apiLoader/user";

export type SurveyQuestion = {
  question: string
  is_required: boolean
  options: Array<string>
  id: string
  type: "choice" | "multiple-choice" | "text"
}

export const getSurveyCopy = (
  copy: Record<string, string>,
  question: SurveyQuestion
) => {
  const headerCopy = copy?.headerCopy ? copy.headerCopy : (question?.question || "");
  const subHeaderCopy = copy?.headerCopy
    ? (question?.question || "")
    : ((question.type === "text" ? copy?.textSubHeaderCopy : copy?.choiceSubHeaderCopy) || "");

  return {
    ...copy,
    headerCopy,
    subHeaderCopy
  };
};

const SURVEY_KEY_MAP: Record<string, string> = {
  survey: "survey_questions",
  "follow-up-survey": "follow_up_survey"
};

const SURVEY_TYPE_MAP: Record<string, string> = {
  survey: SURVEY_TYPES.REGULAR_SURVEY,
  "follow-up-survey": SURVEY_TYPES.FOLLOW_UP_SURVEY
};

export const initSurvey = (settings: SettingsStore, currentSurveyRoute: string = "/survey") => {
  const answers: Record<string, Array<string>> = {};
  const store = get(surveyStore);

  const surveySettingsKey = SURVEY_KEY_MAP[currentSurveyRoute.replace("/", "")];

  ((settings?.[surveySettingsKey] as SurveyQuestion[]) || []).forEach(
    (question: SurveyQuestion, index: number) => {
      answers[index] = Array(question?.options?.length || 0).fill(undefined) as Array<string>;
    }
  );

  const initialStore = {
    currentIndex: store?.currentIndex ?? 0,
    questions: !isEmpty(store?.questions) ? store?.questions : settings?.[surveySettingsKey] as SurveyQuestion[],
    answers: !isEmpty(store?.answers) ? store.answers : answers,
    type: SURVEY_TYPE_MAP[currentSurveyRoute.replace("/", "")]
  };

  updateSurvey(initialStore);
};

export const answerSurveyQuestion = (
  questionIndex: number,
  optionIndex: number,
  option: string,
  type: string
) => {
  const store = get(surveyStore);

  if (type === "multiple-choice") {
    store.answers[questionIndex][optionIndex] = store.answers[questionIndex][optionIndex] ? "" : option;
  } else if (type === "choice") {
    store.answers[questionIndex] = store.answers[questionIndex].map((_, index) => {
      return index === optionIndex ? option : "";
    });
  } else {
    store.answers[questionIndex] = [option];
  }

  updateSurvey(store);
};

export const backHandler = () => {
  const store = get(surveyStore);
  store.currentIndex = (store.currentIndex as number) - 1;
  updateSurvey(store);
};

const computedUserSettings = (store: SurveyStore) => {
  const questionIndex = store.currentIndex as number;
  const question = store.questions?.[questionIndex] as SurveyQuestion;

  const settings: Record<string, unknown> = {};
  let value: string;

  if (question.type === "multiple-choice") {
    value = store.answers[questionIndex].filter(Boolean).join(",");
  } else {
    value = store.answers[questionIndex].find((value) => !!value) || "";
  }

  settings[question.id] = !isEmpty(value) ? value : null;
  return settings;
};

export const nextHandler = async () => {
  const store = get(surveyStore);
  const isLastQuestion = store?.questions?.length === (store.currentIndex as number) + 1;

  const settings = computedUserSettings(store);
  void (await saveUserSettings(settings));

  if (isLastQuestion && store.type === SURVEY_TYPES.REGULAR_SURVEY) {
    updateSurvey({
      isComplete: true
    });
    navigateTo(NavigationType.Next);
  } else if (isLastQuestion && store.type === SURVEY_TYPES.FOLLOW_UP_SURVEY) {
    store.isComplete = true;
    updateSurvey(store);
  } else {
    store.currentIndex = (store.currentIndex as number) + 1;
    updateSurvey(store);
  }
};

export const isFollowUpSurvey = () => {
  const store = get(surveyStore);
  return store.type === SURVEY_TYPES.FOLLOW_UP_SURVEY;
};
